import { fitTitle, fitDescription } from "@/lib/seo-text";
import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";

import { FIRM } from "@/lib/firm";
import { GOOGLE_REVIEWS, GOOGLE_REVIEW_URL } from "@/lib/google-reviews";
import {
  listAllReviews,
  listPublishedReviews,
  setReviewPublished,
  submitReview,
  type ModeratedReview,
  type PublishedReview,
} from "@/lib/reviews.functions";



export const Route = createFileRoute("/reviews")({
  loader: async () => await listPublishedReviews(),
  head: () => ({
    meta: [
      { title: fitTitle("Client Reviews | Coastside Law — Golden Beach & Scarborough") },
      {
        name: "description",
        content:
          fitDescription("Read reviews from Coastside Law clients across Caloundra, Golden Beach, Pelican Waters, Baringa and the Redcliffe peninsula — and leave a review of your own."),
      },
      { property: "og:title", content: fitTitle("Client Reviews | Coastside Law") },
      {
        property: "og:description",
        content:
          fitDescription("Reviews from conveyancing, wills and estate clients of Coastside Law in Queensland."),
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
    links: [{ rel: "canonical", href: "https://coastsidelaw.com.au/reviews" }],
  }),
  component: ReviewsPage,
});

const field =
  "w-full rounded-sm border border-input bg-card px-3 py-2.5 text-sm text-foreground outline-none transition-colors placeholder:text-muted-foreground/70 focus:border-ring focus:ring-2 focus:ring-ring/20";
const labelCls = "block text-sm font-medium text-foreground";

function Stars({ rating }: { rating: number }) {
  return (
    <span aria-label={`${rating} out of 5`} className="text-primary">
      {"★".repeat(rating)}
      <span className="text-muted-foreground/50">{"★".repeat(5 - rating)}</span>
    </span>
  );
}

function ReviewsPage() {
  const reviews = Route.useLoaderData() as PublishedReview[];
  const [rating, setRating] = useState(5);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const fd = new FormData(form);
    setSending(true);
    try {
      await submitReview({
        data: {
          name: String(fd.get("name") ?? ""),
          email: String(fd.get("email") ?? ""),
          suburb: String(fd.get("suburb") ?? ""),
          matterType: String(fd.get("matterType") ?? ""),
          rating,
          body: String(fd.get("body") ?? ""),
          consent: fd.get("consent") === "on",
          sourcePage: "/reviews",
        },
      });
      setSent(true);
      form.reset();
      toast.success("Thank you — your review has come through to us.");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not send your review.");
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="mx-auto max-w-4xl px-4 py-14 sm:px-6">
      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">
        Client feedback
      </p>
      <h1 className="mt-2 text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">
        Reviews of {FIRM.name}
      </h1>
      <p className="mt-3 max-w-2xl text-sm leading-relaxed text-muted-foreground">
        Feedback from clients we have acted for on conveyancing, wills, estates and business
        matters across Caloundra, Golden Beach, Pelican Waters, Baringa, Scarborough and the
        Redcliffe peninsula. Reviews appear here once we have checked them.
      </p>

      <a
        href={GOOGLE_REVIEW_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="mt-6 inline-flex h-[42px] items-center justify-center rounded-sm border border-border px-5 text-sm font-medium text-foreground transition-colors hover:bg-muted"
      >
        Leave a review on Google
      </a>

      <section className="mt-12">
        <h2 className="text-xl font-semibold tracking-tight text-foreground">
          From our Google reviews
        </h2>
        <ul className="mt-5 space-y-4">
          {GOOGLE_REVIEWS.map((review) => (
            <li key={review.id} className="rounded-sm border border-border bg-card p-5">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <p className="text-sm font-semibold text-foreground">
                  {review.name}
                  <span className="font-normal text-muted-foreground"> · {review.postedAt}</span>
                </p>
                <Stars rating={review.rating} />
              </div>
              <p className="mt-3 text-sm leading-relaxed text-foreground">{review.body}</p>
              <a
                href={GOOGLE_REVIEW_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-3 inline-block text-xs font-medium text-muted-foreground underline underline-offset-4 hover:text-foreground"
              >
                Read this review on Google
              </a>
            </li>
          ))}
        </ul>
      </section>

      <section className="mt-12">
        <h2 className="text-xl font-semibold tracking-tight text-foreground">What clients say</h2>
        {reviews.length === 0 ? (
          <p className="mt-3 text-sm text-muted-foreground">
            Published reviews will appear here. If we have acted for you, we would be grateful for
            your feedback using the form below.
          </p>
        ) : (
          <ul className="mt-5 space-y-4">
            {reviews.map((review) => (
              <li key={review.id} className="rounded-sm border border-border bg-card p-5">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <p className="text-sm font-semibold text-foreground">
                    {review.name}
                    {review.suburb ? (
                      <span className="font-normal text-muted-foreground"> · {review.suburb}</span>
                    ) : null}
                  </p>
                  <Stars rating={review.rating} />
                </div>
                {review.matterType ? (
                  <p className="mt-1 text-xs uppercase tracking-wider text-muted-foreground">
                    {review.matterType}
                  </p>
                ) : null}
                <p className="mt-3 text-sm leading-relaxed text-foreground">{review.body}</p>
              </li>
            ))}
          </ul>
        )}
      </section>


      <section id="leave-a-review" className="mt-12 rounded-sm border border-border bg-muted/40 p-6">
        <h2 className="text-xl font-semibold tracking-tight text-foreground">Leave a review</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Your review comes straight through to us. We only publish it on this page if you give us
          permission below.
        </p>

        {sent ? (
          <p className="mt-5 rounded-sm border border-border bg-card p-4 text-sm text-foreground">
            Thank you — we have received your review.
          </p>
        ) : (
          <form className="mt-5 grid gap-4 sm:grid-cols-2" onSubmit={onSubmit}>
            <div>
              <label className={labelCls} htmlFor="review-name">
                Your name
              </label>
              <input id="review-name" name="name" required className={`${field} mt-1.5`} />
            </div>
            <div>
              <label className={labelCls} htmlFor="review-email">
                Email
              </label>
              <input
                id="review-email"
                name="email"
                type="email"
                required
                className={`${field} mt-1.5`}
              />
            </div>
            <div>
              <label className={labelCls} htmlFor="review-suburb">
                Suburb
              </label>
              <input
                id="review-suburb"
                name="suburb"
                placeholder="Golden Beach"
                className={`${field} mt-1.5`}
              />
            </div>
            <div>
              <label className={labelCls} htmlFor="review-matter">
                What did we help with?
              </label>
              <select id="review-matter" name="matterType" className={`${field} mt-1.5`}>
                <option value="Conveyancing — purchase">Conveyancing — purchase</option>
                <option value="Conveyancing — sale">Conveyancing — sale</option>
                <option value="Wills &amp; estates">Wills &amp; estates</option>
                <option value="Probate &amp; estate administration">
                  Probate &amp; estate administration
                </option>
                <option value="Business or commercial">Business or commercial</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div className="sm:col-span-2">
              <span className={labelCls}>Rating</span>
              <div className="mt-1.5 flex gap-1.5">
                {[1, 2, 3, 4, 5].map((value) => (
                  <button
                    key={value}
                    type="button"
                    aria-label={`${value} star${value === 1 ? "" : "s"}`}
                    onClick={() => setRating(value)}
                    className={`h-9 w-9 rounded-sm border text-lg leading-none transition-colors ${
                      value <= rating
                        ? "border-primary bg-primary/10 text-primary"
                        : "border-border text-muted-foreground hover:bg-muted"
                    }`}
                  >
                    ★
                  </button>
                ))}
              </div>
            </div>
            <div className="sm:col-span-2">
              <label className={labelCls} htmlFor="review-body">
                Your review
              </label>
              <textarea
                id="review-body"
                name="body"
                rows={5}
                required
                minLength={20}
                className={`${field} mt-1.5`}
              />
            </div>
            <label className="flex items-start gap-2 text-sm text-muted-foreground sm:col-span-2">
              <input type="checkbox" name="consent" className="mt-1" defaultChecked />
              <span>
                I am happy for {FIRM.name} to publish this review, with my first name and suburb, on
                its website.
              </span>
            </label>
            <div className="sm:col-span-2">
              <button
                type="submit"
                disabled={sending}
                className="inline-flex h-[42px] items-center justify-center rounded-sm bg-primary px-6 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-60"
              >
                {sending ? "Sending…" : "Send review"}
              </button>
            </div>
          </form>
        )}
      </section>

      <ModerationPanel />
    </div>
  );
}

function ModerationPanel() {
  const router = useRouter();
  const [code, setCode] = useState("");
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState<ModeratedReview[] | null>(null);
  const [busy, setBusy] = useState(false);

  async function load(accessCode: string) {
    setBusy(true);
    try {
      const data = await listAllReviews({ data: { code: accessCode } });
      setRows(data);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not load reviews.");
    } finally {
      setBusy(false);
    }
  }

  async function toggle(id: string, published: boolean) {
    setBusy(true);
    try {
      await setReviewPublished({ data: { code, id, published } });
      setRows((current) =>
        (current ?? []).map((row) => (row.id === id ? { ...row, published } : row)),
      );
      toast.success(published ? "Review published." : "Review hidden.");
      router.invalidate();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not update that review.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="mt-14 border-t border-border pt-6">
      {!open ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="text-xs text-muted-foreground hover:text-foreground"
        >
          Firm access
        </button>
      ) : (
        <div>
          <h2 className="text-sm font-semibold text-foreground">Review approvals</h2>
          <form
            className="mt-3 flex flex-wrap gap-2"
            onSubmit={(event) => {
              event.preventDefault();
              void load(code);
            }}
          >
            <input
              type="password"
              value={code}
              onChange={(event) => setCode(event.target.value)}
              placeholder="Access code"
              className={`${field} max-w-[220px]`}
            />
            <button
              type="submit"
              disabled={busy || code.length === 0}
              className="inline-flex h-[42px] items-center rounded-sm bg-primary px-5 text-sm font-medium text-primary-foreground disabled:opacity-60"
            >
              {busy ? "Loading…" : "Load reviews"}
            </button>
          </form>

          {rows ? (
            rows.length === 0 ? (
              <p className="mt-4 text-sm text-muted-foreground">No reviews submitted yet.</p>
            ) : (
              <ul className="mt-5 space-y-3">
                {rows.map((row) => (
                  <li key={row.id} className="rounded-sm border border-border bg-card p-4">
                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                      <p className="text-sm font-semibold text-foreground">
                        {row.name}
                        {row.suburb ? (
                          <span className="font-normal text-muted-foreground"> · {row.suburb}</span>
                        ) : null}
                      </p>
                      <Stars rating={row.rating} />
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {row.email ?? "no email"} ·{" "}
                      {row.createdAt ? new Date(row.createdAt).toLocaleDateString("en-AU") : ""} ·{" "}
                      {row.published ? "Published" : "Awaiting approval"}
                    </p>
                    <p className="mt-2 text-sm leading-relaxed text-foreground">{row.body}</p>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => void toggle(row.id, !row.published)}
                      className="mt-3 inline-flex h-9 items-center rounded-sm border border-border px-4 text-sm font-medium text-foreground transition-colors hover:bg-muted disabled:opacity-60"
                    >
                      {row.published ? "Hide from site" : "Approve & publish"}
                    </button>
                  </li>
                ))}
              </ul>
            )
          ) : null}
        </div>
      )}
    </section>
  );
}

